/**
 * The success screen (`ProductOverview.md` §4.5).
 *
 * "A brief, understated success screen." The work is done and the people are looking at each
 * other, not at a phone — so this says one thing and offers three, in strict order of how much
 * the user is likely to care:
 *
 *   1. **did you actually find each other?** — the only question the proximity check cannot
 *      answer, and the one the north-star number depends on. Two taps, then it gets out of
 *      the way.
 *   2. **pin this spot** — you just proved you will want to come back here. The car, the tent,
 *      the gate. Offering it later would mean offering it after the moment has passed.
 *   3. **install** — softly, once, exactly when the product has proved its worth (§4.8).
 */

import { el } from '../../lib/dom';
import { navigate } from '../../lib/navigate';
import { feedbackForm } from '../feedback';

export interface ConvergedScreenOptions {
  peerName: string | null;
  /** Shown once, softly, after a session has proved its worth (§4.8). */
  install?: HTMLElement | null;
  /** How many people were in the room. A count for the questionnaire, never an identity. */
  groupSize?: number;
  usedRallyPoint?: boolean;
  /**
   * Save where this happened, on this device only.
   *
   * Absent when there is nothing to save — no usable fix at the moment of arrival.
   */
  onPinThisSpot?: (() => void) | null;
}

export function convergedScreen(options: ConvergedScreenOptions): HTMLElement {
  const extras = el('div', { class: 'converged__extras' });

  /** After the questionnaire is answered, it is replaced by the things you might want next. */
  const showExtras = (): void => {
    const actions = el('div', { class: 'screen__actions' });

    if (options.onPinThisSpot) {
      const pinButton = el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          'data-action': 'pin-this-spot',
        },
        'Pin this spot',
      );
      pinButton.addEventListener('click', () => {
        options.onPinThisSpot?.();
        pinButton.textContent = 'Pinned — it is on this phone only';
        pinButton.setAttribute('disabled', '');
      });
      actions.append(pinButton);
    }

    actions.append(
      el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          onclick: () => navigate('/'),
        },
        'Done',
      ),
    );

    extras.replaceChildren(options.install ?? el('span', { hidden: true }), actions);
  };

  extras.replaceChildren(
    feedbackForm({
      groupSize: options.groupSize ?? null,
      usedRallyPoint: options.usedRallyPoint === true,
      onDone: showExtras,
    }),
  );

  return el(
    'main',
    { class: 'screen screen--centered converged' },
    el('div', { class: 'converged__mark', 'aria-hidden': 'true' }),
    el('h1', {
      class: 'screen__title',
      text: options.peerName ? `You found ${options.peerName}` : 'You found each other',
    }),
    extras,
  );
}
